/**
 * ShareDialog — modal de compartilhamento (porta de `showShareButtons` [application.js:7895]).
 * Dois contextos (ambassadorStore.share):
 *  - ambassador: select de ferramenta (eminer/prospecta | leadmachine/prospectawhats) + botões
 *    de rede; a URL é o checkout da ferramenta com `#slug` e a mensagem traz o desconto.
 *  - content: link do vídeo (LeadTalk/LeadMusic) com a mensagem de compartilhamento do conteúdo.
 * #window > .context.share-dialog (420px) > h2 + select? + 6 botões .share (clipboard/whatsapp/
 * telegram/twitter/facebook/linkedin). clipboard copia; os demais abrem a rede em nova aba.
 * Botões no padrão "Escolha de canal" do guia (06-botoes): logo do próprio painel e dica do guia.
 * Copiar troca o ícone por ✓ por 2 s e diz "Link copiado." embaixo (09-retorno). Ao abrir, o
 * foco vai ao primeiro canal; Esc fecha e devolve o foco a quem abriu.
 *
 * Movimento (23/10/2026, guia/10 "Janela e diálogo" e guia/06 "Copiar"):
 *  - véu (.ds-veu) e janela (.ds-dialogo) com .is-aberto: entram em 200 ms (a janela sobe 8 px
 *    e vai de 0,97 a 1) e saem em 140 ms; a janela fica montada enquanto sai e o foco volta a
 *    quem abriu logo que ela começa a sair;
 *  - copiar: o ícone vira ✓ no mesmo lugar (.ds-troca, 140 ms, 0,8 → 1) e volta em 2 s
 *    (--permanencia-2000); clicar de novo copia de novo e recomeça o tempo, sem nova animação;
 *  - os canais respondem só na cor (.ds-resposta).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { useAmbassadorStore, type ShareContext } from './ambassadorStore';
import { ShareIcon } from './shareIcons';
import { buildShareHref, SHARE_TYPES, type ShareType } from '../../lib/shareLinks';
import { externalUrls } from '../../config/env';
import { emitActivity } from '../../lib/activity';

const PROSPECTA_CHECKOUT = 'https://checkout.prospectawhats.com/?prices=65cb6b74e651e90c097304b7';
const PROSPECTAWHATS_CHECKOUT = 'https://maquinadeleads.com/prospecta-whats/';
const YOUTUBE_WATCH = 'https://www.youtube.com/watch?v=';

/** Tempo do ✓ depois de copiar (--permanencia-2000; o token vem em segundos no CSS). */
const COPIA_PERMANENCIA_MS = 2000;

export function ShareDialog(): JSX.Element | null {
  const share = useAmbassadorStore((s) => s.share);
  const closeShare = useAmbassadorStore((s) => s.closeShare);
  // Fica montada enquanto sai (140 ms), com o conteúdo da última abertura.
  const presenca = usePresenca(Boolean(share), '--duracao-140');
  const [ultimo, setUltimo] = useState<ShareContext | null>(share);
  if (share && share !== ultimo) setUltimo(share);
  const atual = share ?? ultimo;
  // Montada a cada abertura: ferramenta, retorno da cópia e quem abriu começam do zero. Se
  // reabrir durante a saída, a mesma janela volta do meio e zera isso tudo (ShareWindow).
  return presenca.montado && atual ? (
    <ShareWindow share={atual} aberto={presenca.aberto} fechando={!share} onClose={closeShare} />
  ) : null;
}

function ShareWindow({
  share,
  aberto,
  fechando,
  onClose,
}: {
  share: ShareContext;
  /** .is-aberto no véu e na janela (transição de entrada e de saída). */
  aberto: boolean;
  /** A janela já foi fechada e está saindo. */
  fechando: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const brand = useBrand();
  const isLeadMachine = brand.source === 'lead-machine';
  // Quem tinha o foco antes de abrir (lido no primeiro render, antes do foco ir ao primeiro canal).
  const openerRef = useRef(document.activeElement as HTMLElement | null);
  const windowRef = useRef<HTMLDivElement | null>(null);
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle');

  // Ferramentas do select (só no contexto de embaixador).
  const tools = isLeadMachine
    ? [
        { value: 'leadmachine', label: t('leadmachine', 'Máquina de Leads') },
        { value: 'prospectawhats', label: t('prospecta', 'Prospecta Whats') },
      ]
    : [
        { value: 'eminer', label: t('eminer', 'eMiner') },
        { value: 'prospecta', label: t('prospecta', 'Prospecta Whats') },
      ];
  const [tool, setTool] = useState(tools[0]!.value);

  // Reaberta durante a saída (os 140 ms em que ela fica montada): a mesma janela volta do meio,
  // sem remontar, e começa do zero como uma abertura nova (ferramenta e retorno da cópia).
  const [fechandoAntes, setFechandoAntes] = useState(fechando);
  if (fechando !== fechandoAntes) {
    setFechandoAntes(fechando);
    if (!fechando) {
      setCopy('idle');
      setTool(tools[0]!.value);
    }
  }

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A janela nasce fechada (.ds-dialogo invisível) e ganha .is-aberto um quadro depois: o foco
  // vai ao primeiro canal quando ela fica visível, se ainda não estiver dentro dela.
  useEffect(() => {
    if (aberto && !windowRef.current?.contains(document.activeElement)) firstRef.current?.focus();
  }, [aberto]);

  // Ao fechar, o foco volta a quem abriu assim que a janela começa a sair (não espera os
  // 140 ms da saída). Reaberta durante a saída, quem abriu passa a ser quem tem o foco agora
  // (a janela está invisível, o foco não está nela) e o tempo do ✓ antigo para.
  const fechouRef = useRef(false);
  useEffect(() => {
    if (fechando) {
      fechouRef.current = true;
      openerRef.current?.focus?.();
    } else if (fechouRef.current) {
      fechouRef.current = false;
      openerRef.current = document.activeElement as HTMLElement | null;
      clearTimeout(timerRef.current);
    }
  }, [fechando]);

  // E na desmontagem de verdade (a janela já saiu da página), se ainda não voltou: o StrictMode
  // roda a limpeza com ela ainda na tela.
  useEffect(() => {
    const opener = openerRef.current;
    const node = windowRef.current;
    return () => {
      clearTimeout(timerRef.current);
      if (!node?.isConnected && document.activeElement !== opener) opener?.focus?.();
    };
  }, []);

  /** Resolve URL + mensagem conforme o contexto (legado 7900-7935). */
  function resolve(): { url: string; message?: string } {
    if (share.kind === 'ambassador') {
      let url: string = externalUrls.buyLeadMachine;
      let message = t('message_share_leadmachine', 'Use meu link e ganhe {#}% de desconto!');
      if (tool === 'eminer') {
        url = externalUrls.buyEminer;
        message = t('message_share_eminer', 'Use meu link e ganhe {#}% de desconto!');
      } else if (tool === 'prospecta') {
        url = PROSPECTA_CHECKOUT;
        message = t('message_share_prospecta', 'Use meu link e ganhe {#}% de desconto!');
      } else if (tool === 'prospectawhats') {
        url = PROSPECTAWHATS_CHECKOUT;
        message = t('message_share_prospecta', 'Use meu link e ganhe {#}% de desconto!');
      }
      message = message.replace('{#}', String(share.discount));
      url += `#${share.slug}`;
      return { url, message };
    }
    // content (LeadTalk/LeadMusic)
    const url = `${YOUTUBE_WATCH}${share.videoId}`;
    const key = share.format === 'leadtalk' ? 'message_share_leadtalk' : 'message_share_leadmusic';
    const message = t(key, 'Ouça: {title}').replace('{title}', share.title);
    return { url, message };
  }

  function onShare(type: ShareType): void {
    const { url, message } = resolve();
    if (share.kind === 'content') emitActivity('content.shared', { channel: type });
    else emitActivity('referral.link_shared', { channel: type });
    if (type === 'clipboard') {
      clearTimeout(timerRef.current);
      const write = navigator.clipboard?.writeText(url) ?? Promise.reject(new Error('clipboard'));
      write.then(
        () => {
          setCopy('done');
          timerRef.current = setTimeout(() => setCopy('idle'), COPIA_PERMANENCIA_MS);
        },
        () => setCopy('failed'),
      );
      return;
    }
    window.open(buildShareHref(type, url, message), '_blank');
  }

  const status =
    copy === 'done'
      ? t('share_copied', 'Link copiado.')
      : copy === 'failed'
        ? t('share_copy_failed', 'Não deu para copiar. Tente de novo.')
        : '';

  return createPortal(
    <div
      ref={windowRef}
      id="window"
      // O .show do legado (opacidade 1 com !important) saiu: quem mostra o véu é a .ds-veu.
      className={`window noselect ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`context share-dialog ds-dialogo${aberto ? ' is-aberto' : ''}`}
        style={{ width: 420 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
      >
        <button
          type="button"
          className="close ds-resposta"
          aria-label={t('close', 'Fechar')}
          onClick={onClose}
        >
          <span className="material-symbols-rounded middle">close</span>
        </button>
        <h2 id="share-title">{t('share_link', 'Compartilhar link')}</h2>
        <div className="form generate">
          {share.kind === 'ambassador' ? (
            <div className="gap">
              <label htmlFor="share-tool">{t('tool', 'Ferramenta')}</label>
              <select id="share-tool" value={tool} onChange={(e) => setTool(e.target.value)}>
                {tools.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="gap">
            <p className="share__label" id="share-channels">
              {t('choose_share', 'Escolha por onde compartilhar')}
            </p>
            <div className="share__channels" role="group" aria-labelledby="share-channels">
              {SHARE_TYPES.map((type, i) => {
                const name = t(type, type);
                const done = type === 'clipboard' && copy === 'done';
                return (
                  <button
                    key={type}
                    ref={i === 0 ? firstRef : undefined}
                    type="button"
                    className={`share ${type} ds-resposta`}
                    aria-label={name}
                    data-dica={name}
                    onClick={() => onShare(type)}
                  >
                    {type === 'clipboard' ? (
                      // Copiar: o ícone e o ✓ no mesmo lugar, um entrando e o outro saindo.
                      <span className="ds-troca">
                        <span className={done ? 'ds-fora' : undefined}>
                          <ShareIcon type={type} />
                        </span>
                        <span
                          className={`material-symbols-rounded${done ? '' : ' ds-fora'}`}
                          aria-hidden="true"
                        >
                          check
                        </span>
                      </span>
                    ) : (
                      <ShareIcon type={type} />
                    )}
                  </button>
                );
              })}
            </div>
            <p
              className={copy === 'failed' ? 'share__status share__status--erro' : 'share__status'}
              role="status"
            >
              {/* O retorno entra com fade (.ds-aparece), na linha já reservada. */}
              {status ? (
                <span key={copy} className="ds-aparece">
                  {status}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
